import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../redux/store";
import { ISearchAPIPayload } from "../redux/types/common";
import TableList from "../Components/TableList";
import TableHeader from "../Components/TableHeader";
import FinesReportForm from "../Components/reports/FinesReportForm";
import {
  getReportData,
  onChangeReportsList,
} from "../redux/slices/reports.slice";
import {
  agentCollectionLogColumns,
  agentCollectionSummaryColumns,
  collectionColumns,
  fineReportColumns,
  statePerformanceColumns,
} from "../utils/constants";
import { useCallback, useEffect, useState } from "react";
import { Tab, Tabs } from "@mui/material";

const AGENT_COLLECTION_TABS = ["Summary", "Entries-Logs"] as const;

const Reports = () => {
  const { loading, pageProps, data } = useSelector(
    (state: RootState) => state.reports,
  );

  const dispatch = useDispatch<AppDispatch>();
  const [agentCollectionTab, setAgentCollectionTab] = useState(0);

  useEffect(() => {
    setAgentCollectionTab(0);
  }, [pageProps.reportType]);

  const getTableCols = useCallback(() => {
    return pageProps.reportType === "Fines Report"
      ? fineReportColumns
      : pageProps.reportType === "Collection Report" ||
          pageProps.reportType === "Agent Collection Report"
        ? collectionColumns
        : statePerformanceColumns;
  }, [pageProps.reportType]);

  const hasReportData =
    (data?.totalRecords ?? 0) > 0 || (data?.summary?.length ?? 0) > 0;

  const onLogPageChange = (payload: ISearchAPIPayload) => {
    dispatch(
      getReportData({
        ...payload,
        reportType: pageProps.reportType,
      } as any),
    );
    dispatch(
      onChangeReportsList({
        ...payload,
        reportType: pageProps.reportType,
      }),
    );
  };

  const isAgentCollection =
    pageProps.reportType === "Agent Collection Report" && hasReportData;

  return (
    <div className="min-h-fit p-4 pt-3">
      <TableHeader
        title="Reports"
        onPageChange={(data: ISearchAPIPayload) => {
          dispatch(getReportData(data as any));
          dispatch(
            onChangeReportsList({
              ...data,
            }),
          );
        }}
        loading={loading}
        pageProps={pageProps}
        onRefreshData={(data: ISearchAPIPayload) => {
          dispatch(
            onChangeReportsList({
              ...data,
            }),
          );
        }}
        showSearch={false}
        showAdd={false}
        showRefresh={false}
        otherComponents={<FinesReportForm />}
      />
      {isAgentCollection && (
        <>
          <Tabs
            value={agentCollectionTab}
            onChange={(_, value) => setAgentCollectionTab(value)}
            aria-label="Agent collection report tabs"
            className="border-slate-400 border-b border-neutral-500 mb-4 mt-2"
            variant="scrollable"
            selectionFollowsFocus
            classes={{ root: "tabRoot" }}
          >
            {AGENT_COLLECTION_TABS.map((label) => (
              <Tab
                key={label}
                label={label}
                classes={{
                  root: "tabHeight rounded-t-md",
                  selected: "textWhite bg-primary-light",
                }}
              />
            ))}
          </Tabs>
          {agentCollectionTab === 0 ? (
            <TableList
              columns={agentCollectionSummaryColumns}
              onPageChange={() => {}}
              loading={loading}
              showPagination={false}
              pageProps={{
                ...pageProps,
                pageNumber: 1,
                perPage: data.summary?.length || 10,
              }}
              apiResponse={{
                list: data.summary ?? [],
                totalRecords: data.summary?.length ?? 0,
                totalPages: 1,
              }}
            />
          ) : (
            <TableList
              columns={agentCollectionLogColumns}
              onPageChange={onLogPageChange}
              loading={loading}
              pageProps={pageProps}
              apiResponse={data}
            />
          )}
        </>
      )}
      {hasReportData && !isAgentCollection && (
        <TableList
          columns={getTableCols()}
          onPageChange={onLogPageChange}
          loading={loading}
          pageProps={pageProps}
          apiResponse={data}
        />
      )}
    </div>
  );
};

export default Reports;
